<template>
	<div class="node-net-container">
		<div :id="id" ref="nodenet" class="node-net" />
		<slot />
		<div :open="overlay" class="node-net-overlay" @click.stop="togglePopOver($event)" />
	</div>
</template>
<script>
/* eslint-disable vue/no-unused-components */
import * as d3 from "d3";
import Vue, { defineComponent } from "vue";
import Icon from "./../../Icon/Icon";
import { createPopper } from "@popperjs/core";

export default defineComponent({
	name: "Cluster",
	components: {
		Icon
	},
	props: {
		nodes: {
			type: Array,
			default: () => []
		},
		nodeRadius: {
			type: Number,
			default: 30
		},
		nodeNames: {
			type: Boolean,
			default: true
		},
		resetOnDrag: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			id: "nodeNetwork",
			lineageRef: null,
			currentPopper: null,
			overlay: false
		};
	},
	watch: {
		nodes: {
			handler: "init",
			deep: true
		},
		links: {
			handler: "init",
			deep: true
		},
		nodeRadius: {
			handler: "init"
		},
		nodeNames: {
			handler() {
				document.querySelectorAll(`#${this.id} .net-node-labels`).forEach(label => {
					if (!this.nodeNames) {
						label.classList.add("hide");
					} else {
						label.classList.remove("hide");
					}
				});
			}
		}
	},
	beforeMount() {
		this.id = this.generateId();
		/**
		 * Register component for Node
		 */
		this.icon = Vue.extend(Icon);
	},
	mounted() {
		this.init();
	},
	methods: {
		appendArrows(svg) {
			/**
			 * Arrow for links
			 */
			svg
				.append("svg:defs")
				.append("svg:marker")
				.attr("id", "arrow")
				.attr("viewBox", "0 -8 16 16")
				.attr("refX", 15)
				.attr("refY", 0)
				.attr("markerWidth", 8)
				.attr("markerHeight", 8)
				.attr("orient", "auto")
				.attr("fill", "var(--dashboard-font-light)")
				.append("svg:path")
				.attr("d", "M0,-8L16,0L0,8");
			svg
				.append("svg:defs")
				.append("svg:marker")
				.attr("id", "arrow-error")
				.attr("viewBox", "0 -8 16 16")
				.attr("refX", 15)
				.attr("refY", 0)
				.attr("markerWidth", 8)
				.attr("markerHeight", 8)
				.attr("orient", "auto")
				.attr("fill", "var(--dashboard-danger)")
				.append("svg:path")
				.attr("d", "M0,-8L16,0L0,8");
			svg
				.append("svg:defs")
				.append("svg:marker")
				.attr("id", "arrow-warning")
				.attr("viewBox", "0 -8 16 16")
				.attr("refX", 15)
				.attr("refY", 0)
				.attr("markerWidth", 8)
				.attr("markerHeight", 8)
				.attr("orient", "auto")
				.attr("fill", "var(--dashboard-warning)")
				.append("svg:path")
				.attr("d", "M0,-8L16,0L0,8");
		},
		/**
		 * Delete when nodes accepted through props
		 */
		getGraphData() {
			const nodes = [];
			const links = [];
			function addLinks(child, parent) {
				if (child.relation && typeof child.relation === "string") {
					links.push({ source: parent.id, target: child.id, label: child.relation });
				} else if (child.relation && typeof child.relation === "object") {
					/**
					 * From parent relation
					 */
					if (child.relation.from && typeof child.relation.from === "string") {
						links.push({ source: parent.id, target: child.id, label: child.relation.from });
					} else if (child.relation.from && typeof child.relation.from === "object") {
						links.push({
							source: parent.id,
							target: child.id,
							label: child.relation.from.name,
							state: child.relation.from.state
						});
					}
					/**
					 * to parent relation
					 */
					if (child.relation.to && typeof child.relation.to === "string") {
						links.push({ source: child.id, target: parent.id, label: child.relation.to });
					} else if (child.relation.to && typeof child.relation.to === "object") {
						links.push({
							source: child.id,
							target: parent.id,
							label: child.relation.to.name,
							state: child.relation.to.state
						});
					}
				}
				/**
				 * Checking for other relations
				 */
				if (typeof child.otherRelations === "object" && Array.isArray(child.otherRelations)) {
					child.otherRelations.forEach(r => {
						const source = Object.keys(r)[0];

						if (r[source] && typeof r[source] === "string") {
							links.push({ source, target: child.id, label: r[source] });
						} else if (r[source] && typeof r[source] === "object") {
							links.push({
								source,
								target: child.id,
								label: r[source].name,
								state: r[source].state
							});
						}
					});
				}
			}
			function addNodes(nodelist, parent) {
				nodelist.forEach(n => {
					addLinks(n, parent);

					if (n.children && n.children.length > 0) {
						addNodes(n.children, n);
						if (n.collapsed) {
							n._children = n.children;
							n.children = null;
						}
					}
					nodes.push(n);
				});
			}

			addNodes(this.nodes, null);

			return {
				nodes,
				links
			};
		},
		getComputedLinks(linkContainer) {
			return linkContainer
				.append("svg:path")
				.attr("class", "node-link")
				.attr("fill", "transparent")
				.attr("stroke-width", "1px")
				.attr("marker-end", function (d) {
					if (d.state === "error") {
						return "url(#arrow-error)";
					} else if (d.state === "warning") {
						return "url(#arrow-warning)";
					}
					return "url(#arrow)";
				})
				.attr("id", function (d) {
					return `${d.source.id.replace(/ /g, "_")}-${d.target.id.replace(/ /g, "_")}`;
				})
				.attr("stroke", function (d) {
					if (d.state === "error") {
						return "var(--dashboard-danger)";
					} else if (d.state === "warning") {
						return "var(--dashboard-warning)";
					} else if (d.state === "primary") {
						return "var(--dashboard-primary)";
					} else if (d.state === "success") {
						return "var(--dashboard-success)";
					} else if (d.state === "selected") {
						return "var(--dashboard-font-dark)";
					}
					return "var(--dashboard-font-light)";
				});
		},
		getLinkLabels(linkContainer) {
			/**
			 * Adding link label wrapper
			 */
			linkContainer
				.append("svg:text")
				.attr("dx", 20)
				.attr("dy", 3)
				.append("textPath")
				.attr("class", "link-label-wrapper")
				.attr("text-anchor", "middle")
				.attr("startOffset", "50%")
				.attr("stroke", "var(--dashboard-background)")
				.attr("stroke-width", "12px")
				.attr("xlink:href", function (d) {
					return `#${d.source.id.replace(/ /g, "_")}-${d.target.id.replace(/ /g, "_")}`;
				})
				.text(function (d) {
					return d.label;
				});

			/**
			 * Adding link label
			 */
			return linkContainer
				.append("svg:text")
				.attr("dx", 20)
				.attr("dy", 3)
				.append("textPath")
				.attr("class", "link-label")
				.attr("text-anchor", "middle")
				.attr("startOffset", "50%")
				.attr("fill", function (d) {
					if (d.state === "selected") {
						return "var(--dashboard-font-dark)";
					}
					return "var(--dashboard-font-light)";
				})
				.attr("xlink:href", function (d) {
					return `#${d.source.id.replace(/ /g, "_")}-${d.target.id.replace(/ /g, "_")}`;
				})
				.text(function (d) {
					return d.label;
				});
		},
		getNodes(container, graph, nodeRadius) {
			return container
				.append("g")
				.attr("class", "net-nodes")
				.selectAll("g")
				.data(graph.nodes)
				.enter()
				.append("circle")
				.attr("class", "node-circle")
				.attr("r", nodeRadius)
				.attr("id", function (d) {
					return d.id.replace(/ /g, "_");
				})
				.attr("fill", function (d) {
					if (d.root) {
						return "var(--dashboard-primary)";
					}
					return "var(--dashboard-element)";
				});
		},
		getNodeLabels(container, graph) {
			return container
				.append("g")
				.attr("class", "net-node-labels")
				.classed("hide", !this.nodeNames)
				.selectAll("g")
				.data(graph.nodes)
				.enter()
				.append("text")
				.attr("text-anchor", "middle")
				.attr("class", "node-label")
				.text(function (d) {
					return d.name;
				});
		},
		getNodeStates(container, graph, nodeRadius) {
			return container
				.append("g")
				.attr("class", "net-node-states")
				.selectAll("g")
				.data(graph.nodes)
				.enter()
				.append("circle")
				.attr("class", "node-state-circle")
				.attr("r", nodeRadius - 3)
				.attr("fill", "transparent")
				.attr("stroke", function (d) {
					if (d.state === "error") {
						return "var(--dashboard-danger)";
					} else if (d.state === "warning") {
						return "var(--dashboard-warning)";
					} else if (d.state === "primary") {
						return "var(--dashboard-primary)";
					} else if (d.state === "success") {
						return "var(--dashboard-success)";
					} else if (d.state === "selected") {
						return "var(--dashboard-font-dark)";
					} else {
						return "transparent";
					}
				})
				.attr("stroke-width", 1);
		},
		getNodeStateCountCircle(container, graph) {
			return container
				.append("g")
				.attr("class", "net-node-state-counts")
				.selectAll("g")
				.data(graph.nodes)
				.enter()
				.append("circle")
				.attr("class", "node-state-count-circle")
				.attr("r", 8)
				.classed("hide", function (d) {
					return !d.collapsed;
				})
				.attr("fill", function (d) {
					if (d.state === "error") {
						return "var(--dashboard-danger)";
					} else if (d.state === "warning") {
						return "var(--dashboard-warning)";
					} else if (d.state === "primary") {
						return "var(--dashboard-primary)";
					} else if (d.state === "success") {
						return "var(--dashboard-success)";
					} else if (d.state === "selected") {
						return "var(--dashboard-font-dark)";
					} else {
						return "var(--dashboard-font-light)";
					}
				});
		},
		getNodeChildCounts(container, graph) {
			return container
				.append("g")
				.attr("class", "net-node-child-counts")
				.selectAll("g")
				.data(graph.nodes)
				.enter()
				.append("text")
				.attr("class", "node-state-child-count")
				.attr("text-anchor", "middle")
				.classed("hide", function (d) {
					return !d.collapsed;
				})
				.text(function (d) {
					if (d._children && d._children.length > 0) {
						return d._children.length;
					}
					return 0;
				});
		},
		getNodeIcons(container, graph, nodeRadius) {
			return container
				.append("g")
				.attr("class", "node-icons")
				.selectAll("g")
				.data(graph.nodes)
				.enter()
				.append("foreignObject")
				.attr("width", nodeRadius * 2)
				.attr("height", nodeRadius * 2)
				.html(d => {
					if (d.icon) {
						let propsData = { type: "filled", effects: false };
						if (typeof d.icon === "string") {
							propsData.name = d.icon;
						} else if (typeof d.icon === "object") {
							propsData = { ...propsData, ...d.icon };
						}
						const iconInstance = new this.icon({
							propsData
						});

						return iconInstance.$mount().$el.outerHTML;
					} else {
						return "";
					}
				});
		},
		getLineage(graph, linkWidth) {
			return (
				d3
					.forceSimulation(graph.nodes)
					.force("charge", d3.forceManyBody().strength(-1500))
					//.force("center", d3.forceCenter(width / 2, height / 2))
					//.force("x", d3.forceX(width / 2).strength(1))
					//.force("y", d3.forceY(height / 2).strength(1))
					.force(
						"link",
						d3
							.forceLink(graph.links)
							.id(function (d) {
								return d.id;
							})
							.distance(linkWidth)
							.strength(1)
					)
					.force("collide", d3.forceCollide(70))
				//	.force("radial", d3.forceRadial(0, 0))
			);
		},
		init() {
			//  const thisComponent = this;
			const nodeRadius = this.nodeRadius;
			const linkWidth = 180;
			/**
			 * Getting height and width of container
			 */
			const width = this.$refs.nodenet.offsetWidth;
			const height = this.$refs.nodenet.offsetHeight;
			/**
			 * Initializing svg
			 */
			const wrapper = document.getElementById(this.id);
			wrapper.innerHTML = "";

			const svg = d3
				.select(`#${this.id}`)
				.append("svg")
				.attr("width", width)
				.attr("height", height);
			const container = svg.append("g");
			/**
			 * Binding zoom
			 */
			svg
				.call(
					d3
						.zoom()
						.scaleExtent([0.1, 4])
						.on("zoom", function () {
							container.attr("transform", d3.event.transform);
						})
				)
				.on("dblclick.zoom", null);

			this.appendArrows(svg);
			const graph = this.getGraphData();
			// eslint-disable-next-line no-unused-vars
			const rootNodeIndex = graph.nodes.findIndex(x => x.root);
			if (rootNodeIndex > -1) {
				graph.nodes[rootNodeIndex].fx = width / 2;
				graph.nodes[rootNodeIndex].fy = height / 2;
			}
			this.lineageRef = this.getLineage(graph, linkWidth).on("tick", updatePositions);

			const adjlist = [];

			graph.links.forEach(function (d) {
				adjlist[`${d.source.index}-${d.target.index}`] = true;
				adjlist[`${d.target.index}-${d.source.index}`] = true;
			});

			function neigh(a, b) {
				return a == b || adjlist[`${a}-${b}`];
			}

			/**
			 * Adding link container
			 */
			const linkContainer = container
				.append("g")
				.attr("class", "links")
				.selectAll("g")
				.data(graph.links)
				.enter()
				.append("g");

			/**
			 * Adding links
			 */
			const computedLinks = this.getComputedLinks(linkContainer);
			/**
			 * Adding labels
			 */
			const labels = this.getLinkLabels(linkContainer);

			/**
			 * Adding nodes
			 */
			const computedNodes = this.getNodes(container, graph, nodeRadius);
			let nodeNames = null;

			/**
			 * Adding node labels
			 */
			nodeNames = this.getNodeLabels(container, graph);

			/**
			 * Adding node icons if any
			 */
			const nodeIcons = this.getNodeIcons(container, graph, nodeRadius);
			/**
			 * Adding node states
			 */
			const computedNodeStates = this.getNodeStates(container, graph, nodeRadius);
			/**
			 * Adding node state count
			 */
			const computedNodeStateCountCircles = this.getNodeStateCountCircle(container, graph);
			/**
			 * display children count
			 */
			const childCounts = this.getNodeChildCounts(container, graph);
			/**
			 * Binding mouseover, mouseout, click events
			 */
			computedNodes
				.on("mouseover", focus)
				.on("mouseout", unfocus)
				.on("click", this.handleClick)
				.on("dblclick", this.handleDbClick)
				.on("contextmenu", this.handleRightClick);

			/**
			 * binding drag: start, drag, end  events
			 */
			computedNodes.call(
				d3.drag().on("start", this.dragstarted).on("drag", this.dragged).on("end", this.dragended)
			);
			function focus() {
				const index = d3.select(d3.event.target).datum().index;
				const nofocusopacity = 0.4;
				computedNodes.style("opacity", function (o) {
					return neigh(index, o.index) ? 1 : nofocusopacity;
				});
				if (nodeNames) {
					nodeNames.style("opacity", function (o) {
						return neigh(index, o.index) ? 1 : nofocusopacity;
					});
				}
				computedNodeStates.style("opacity", function (o) {
					return neigh(index, o.index) ? 1 : nofocusopacity;
				});
				computedNodeStateCountCircles.style("opacity", function (o) {
					return neigh(index, o.index) ? 1 : nofocusopacity;
				});
				childCounts.style("opacity", function (o) {
					return neigh(index, o.index) ? 1 : nofocusopacity;
				});
				nodeIcons.style("opacity", function (o) {
					return neigh(index, o.index) ? 1 : nofocusopacity;
				});
				computedLinks.style("opacity", function (o) {
					// return o.source.index == index || o.target.index == index ? 1 : 0.1;
					return o.source.index == index || o.target.index == index ? 1 : nofocusopacity;
				});
				labels.style("opacity", function (o) {
					// return o.source.index == index || o.target.index == index ? 1 : 0.1;
					return o.source.index == index || o.target.index == index ? 1 : nofocusopacity;
				});
			}

			function unfocus() {
				computedNodes.style("opacity", 1);
				computedNodeStates.style("opacity", 1);
				computedNodeStateCountCircles.style("opacity", 1);
				childCounts.style("opacity", 1);
				if (nodeNames) {
					nodeNames.style("opacity", 1);
				}
				nodeIcons.style("opacity", 1);
				computedLinks.style("opacity", 1);
				labels.style("opacity", 1);
			}

			/**
			 * Fix infinite values
			 */
			function fixna(x) {
				if (isFinite(x)) {
					return x;
				}
				return 0;
			}
			/**
			 * Update positions
			 */
			function updatePositions() {
				computedNodes.call(function (node) {
					node.attr("transform", function (d) {
						return `translate(${fixna(d.x)},${fixna(d.y)})`;
					});
				});
				computedNodeStates.call(function (node) {
					node.attr("transform", function (d) {
						return `translate(${fixna(d.x)},${fixna(d.y)})`;
					});
				});
				computedNodeStateCountCircles.call(function (node) {
					node.attr("transform", function (d) {
						return `translate(${fixna(d.x + nodeRadius - 12)},${fixna(d.y - nodeRadius + 12)})`;
					});
				});
				childCounts.call(function (node) {
					node.attr("transform", function (d) {
						return `translate(${fixna(d.x + nodeRadius - 12)},${fixna(d.y - nodeRadius + 15)})`;
					});
				});
				if (nodeNames) {
					nodeNames.call(function (node) {
						node.attr("transform", function (d) {
							return `translate(${fixna(d.x)},${fixna(d.y + nodeRadius + 14)})`;
						});
					});
				}
				nodeIcons.call(function (icon) {
					icon.attr("transform", function (d) {
						return `translate(${fixna(d.x - nodeRadius)},${fixna(d.y - nodeRadius)})`;
					});
				});
				computedLinks.call(function (link) {
					link.attr("d", function (d) {
						// Total difference in x and y from source to target
						const diffX = d.target.x - d.source.x;
						const diffY = d.target.y - d.source.y;

						// Length of path from center of source node to center of target node
						const pathLength = Math.sqrt(diffX * diffX + diffY * diffY);
						const pathAngle = pathLength - 60;

						const t_radius = nodeRadius + 6; // nodeWidth is just a custom attribute I calculate during the creation of the nodes depending on the node width
						const dx = d.target.x - d.source.x;
						const dy = d.target.y - d.source.y;
						const gamma = Math.atan2(dy, dx); // Math.atan2 returns the angle in the correct quadrant as opposed to Math.atan
						// console.log(gamma, d.source, d.target);
						const tx = d.target.x - Math.cos(gamma) * t_radius;
						const ty = d.target.y - Math.sin(gamma) * t_radius;

						return `M${d.source.x},${d.source.y}A${pathAngle},${pathAngle} 0 0,1 ${+tx},${ty}`;
					});
				});
			}
		},
		handleClick() {
			/**
			 * Whenever any node/ svg element clicked this event emitted
			 */
			this.$emit("click", d3.select(d3.event.target).datum());
		},
		handleRightClick() {
			d3.event.stopPropagation();
			d3.event.preventDefault();

			const d = d3.select(d3.event.target).datum();
			this.togglePopOver(d);
		},
		togglePopOver(d = {}) {
			document.querySelectorAll("[popover-for]").forEach(el => {
				el.style.display = "none";
			});
			const target = document.querySelector(`#${d.id}`);
			const content = document.querySelector(`[popover-for='${d.id}']`);
			if (content) {
				content.style.display = "block";
			}
			if (target && content && this.currentPopper == null) {
				this.overlay = true;
				this.currentPopper = createPopper(target, content, {
					placement: "auto",
					strategy: "fixed",
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [-100, 10]
							}
						}
					]
				});
			} else {
				this.overlay = false;
				if (this.currentPopper) {
					this.currentPopper.destroy();
					this.currentPopper = null;
				}
				if (content) {
					content.style.display = "none";
				}
			}
		},
		handleDbClick() {
			//	console.log("dblclick", d3.select(d3.event.target).datum());
			const d = d3.select(d3.event.target).datum();
			if (d.children) {
				d.collapsed = true;
				d._children = d.children;
				d.children = null;
			} else {
				d.collapsed = false;
				d.children = d._children;
				d._children = null;
			}
			this.init();

			/**
			 * Whenever any node/ svg element double clicked this event emitted
			 */
			this.$emit("dblclick", d3.select(d3.event.target).datum());
		},
		dragstarted(d) {
			d3.event.sourceEvent.stopPropagation();
			if (!d3.event.active) {
				this.lineageRef.alphaTarget(0.3).restart();
			}
			d.fx = d.x;
			d.fy = d.y;
			/**
			 * Whenever node drag started
			 */
			this.$emit("dragstart", d);
		},

		dragged(d) {
			d.fx = d3.event.x;
			d.fy = d3.event.y;

			/**
			 * Whenever node dragged
			 */
			this.$emit("dragged", d);
		},

		dragended(d) {
			if (this.resetOnDrag) {
				if (!d3.event.active) {
					this.lineageRef.alphaTarget(0);
				}
				d.fx = null;
				d.fy = null;
			}

			/**
			 * Whenever node drag ends
			 */
			this.$emit("dragend", d);
		},
		/**
		 * Generate random ID for each Instance
		 */
		generateId() {
			let id = "";
			const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
			const charsLength = chars.length;
			for (let i = 0; i < 10; i++) {
				id += chars.charAt(Math.floor(Math.random() * charsLength));
			}
			return id;
		}
	}
});
</script>
<style lang="scss">
div.node-net-container {
	width: 100%;
	height: 100%;
	align-self: stretch;
	div.node-net {
		width: 100%;
		height: 100%;
		align-self: stretch;
		circle.node-circle {
			stroke: var(--dashboard-background);
			stroke-width: 6px;
			cursor: pointer;
		}
		.node-state-circle {
			pointer-events: none;
		}
		.link-label {
			font-size: 10px;
		}
		.node-label {
			font-size: 10px;
			fill: var(--dashboard-font-dark);
		}
		.node-state-child-count {
			font-size: 10px;
			fill: var(--dashboard-background);
		}
		.hide {
			display: none;
		}
		// .node-link {
		// 	stroke: #7b93b2;
		// }
		foreignObject {
			pointer-events: none;
		}
		.node-icons {
			div.icon-container {
				height: 100%;
				width: 100%;
			}
		}
	}
	.node-popover {
		display: none;
	}

	> div.node-net-overlay {
		content: "";
		position: fixed;
		top: 0px;
		left: 0px;

		background-color: #000;
		transition: opacity 0.3s linear;
		z-index: 103;
		opacity: 0;
		pointer-events: none;
	}
	> div.node-net-overlay {
		&[open] {
			opacity: 0.7;
			pointer-events: auto;
			width: 100%;
			height: 100%;
		}
	}
	> *:not(div.node-net-overlay) {
		z-index: 104;
	}
}
</style>
